"use client"

import React, { useState } from "react"
import { Typography, Button, Drawer } from "@shared/ui.primitives"
import { useTrackElementViewed } from "packages/membership/tracking"
import { dispatch } from "@shared/utils.events"

import * as styles from "./ProductBenefits.css"
import type { CListType } from "../CList/CList"
import { CList } from "../CList/CList"

type ListItemType = {
  title: string
  description: string
}

type Props = {
  title: string
  items: ListItemType[]
  extraDescription?: string
  extraDescriptionTitle?: string
  cList?: CListType
}

const baseTrackingValues = {
  target: "PDP Product Benefits",
  location: "PDP",
}

export const ProductBenefits: React.FC<Props> = ({
  title = "Product Benefits",
  items,
  extraDescription,
  extraDescriptionTitle = "Description",
  cList,
}) => {
  const { ref } = useTrackElementViewed(baseTrackingValues)
  const [isOpen, setIsOpen] = useState(false)

  if (!items?.length) return null

  const firstThreeItems = items.slice(0, 3)

  const showModal = () => {
    dispatch("page element clicked", {
      target: "Product Benefits",
      location: "PDP",
      details: "View All Benefits",
    })
    setIsOpen(true)
  }

  const handleDismiss = () => {
    dispatch("page element clicked", {
      target: "Product Benefits",
      details: "close",
      location: "PDP",
    })
    setIsOpen(false)
  }

  const showViewAllButton = extraDescription || cList || items.length > 3

  return (
    <>
      <div className={styles.container} ref={ref}>
        <Typography as="h4" variant="subheader-4" color="primary-dark">
          {title}
        </Typography>

        <ul className={styles.list}>
          {firstThreeItems.map((item, index) => (
            <li key={index}>
              <Typography as="p" variant="subheader-7" color="primary-medium">
                {item.title}
              </Typography>
              <Typography as="p" variant="body-sm" color="primary-dark">
                {item.description}
              </Typography>
            </li>
          ))}
        </ul>
        {showViewAllButton && (
          <div className={styles.buttonWrapper}>
            <Typography variant="link-xs" color="primary-medium">
              <Button
                width="minContent"
                variant="link"
                onClick={showModal}
                text="View All Benefits"
                size="small"
              ></Button>
            </Typography>
          </div>
        )}
      </div>

      <Drawer
        isOpen={isOpen}
        handleDismiss={handleDismiss}
        ariaDescription="List of benefits of the product"
        ariaLabel="Product Benefits"
        id="product-benefits-drawer"
        testId="product-benefits-drawer"
        headerId="product-benefits-drawer-header"
      >
        <Drawer.Header
          id="product-benefits-drawer-header"
          handleDismiss={handleDismiss}
        >
          {title}
        </Drawer.Header>
        <Drawer.Body>
          <>
            <ul className={styles.list}>
              {items.map((item, index) => (
                <li key={index}>
                  <Typography
                    as="h6"
                    variant="subheader-6"
                    color="primary-dark"
                  >
                    {item.title}
                  </Typography>
                  <Typography as="p" variant="body-md" color="primary-dark">
                    {item.description}
                  </Typography>
                </li>
              ))}
            </ul>

            {extraDescription && (
              <>
                <hr className={styles.separator} />
                <Typography as="h6" variant="subheader-6" color="primary-dark">
                  {extraDescriptionTitle}
                </Typography>
                <Typography as="p" variant="body-md" color="primary-dark">
                  {extraDescription}
                </Typography>
              </>
            )}
            {cList && (
              <>
                <hr className={styles.separator} />
                <CList cList={cList} />
              </>
            )}
          </>
        </Drawer.Body>
        <Drawer.Footer>
          <Button
            width="full"
            variant="secondary"
            onClick={handleDismiss}
            text="Back to product page"
          />
        </Drawer.Footer>
      </Drawer>
    </>
  )
}
